import { useCallback, useMemo } from 'react';
import { Button, Flex, Radio, RadioChangeEvent, Space, Tag, Tooltip, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { t } from 'i18next';

import { Table, notification } from '@ohpen/enso-ui';
import { ActionBarProps, WithRowNumber } from '../interfaces';
import { onAddRow } from '../utils';
import styles from '../styles.module.css';

const { Text } = Typography;

export const ActionBar = <EntityCrudData,>({
  actionType,
  isCrud,
  isEdit,
  isClone,
  isCreate,
  isUpdateChange,
  hideShowIds,
  isLoading,
  state,
  cloneTypeDisabledReason,
  showIdOptions,
  rowsErrorsCount,
  columnsErrorsCount,
  editableColumns,
  valueLists,
  changeRequestData,
  onChangeCloneType,
  dispatch,
}: ActionBarProps<EntityCrudData>) => {
  const handleViewChange = useCallback(
    ({ target: { value } }: RadioChangeEvent) => {
      dispatch({ type: 'setView', view: value });
    },
    [dispatch],
  );

  const onChangeShowIds = useCallback(
    (e: RadioChangeEvent) => {
      dispatch({ type: 'setShowIds', showIds: e.target.value });
    },
    [dispatch],
  );

  const handleAddRow = useCallback(
    () =>
      onAddRow({
        actionId: state.actionId,
        dataSource: state.dataSource as WithRowNumber<EntityCrudData>[] | null,
        dispatch,
        notification,
        t,
      }),
    [dispatch, state.actionId, state.dataSource],
  );

  const viewOptions = useMemo(() => {
    const totalRowsErrors = Object.values(rowsErrorsCount).reduce((acc: number, count: number) => acc + (count ? 1 : 0), 0);
    const totalColumnsErrors = Object.values(columnsErrorsCount).reduce((acc: number, count: number) => acc + (count ? 1 : 0), 0);
    const totalCellsErrors = Object.values(columnsErrorsCount).reduce((acc: number, count: number) => acc + count, 0);
    const totalEditableColumns = isEdit ? Object.values(editableColumns).reduce((acc, editable) => acc + (editable ? 1 : 0), 0) : 0;

    const getLabel = (label: string, number: number, type?: 'error' | 'info') =>
      number ? (
        <>
          <Text>{t(label)}</Text>
          <Tag color={type === 'info' ? 'blue' : 'red'} bordered={false} className={styles.countTag}>
            {number}
          </Tag>
        </>
      ) : (
        t(label)
      );

    const options = [
      { value: 'all', label: t('global.labels.all') },
      { value: 'columns', label: getLabel('global.labels.columnsWithErrors', totalColumnsErrors), disabled: !totalColumnsErrors },
      { value: 'rows', label: getLabel('global.labels.rowsWithErrors', totalRowsErrors), disabled: !totalRowsErrors },
      { value: 'cells', label: getLabel('global.labels.cellsWithErrors', totalCellsErrors), disabled: !totalCellsErrors },
    ];
    if (isClone) options.push({ value: 'rateChange', label: t('pmp.products.labels.rateChange'), disabled: state.cloneType === 'new' });
    if (isEdit)
      options.splice(1, 0, {
        value: 'editable',
        label: getLabel('global.labels.editableColumns', totalEditableColumns, 'info'),
        disabled: !totalEditableColumns,
      });

    return options;
  }, [columnsErrorsCount, editableColumns, isClone, isEdit, rowsErrorsCount, state.cloneType]);

  const cloneTypeOptions = useMemo(
    () => [
      { label: t('pmp.products.labels.newProducts'), value: 'new' },
      { label: t('pmp.products.labels.replacementProducts'), value: 'replacement', disabled: !!cloneTypeDisabledReason },
    ],
    [cloneTypeDisabledReason],
  );

  const cloneTypeTooltipKey = useMemo(() => {
    if (cloneTypeDisabledReason === 'locked') return 'cloneTypeProductsDisabledDesc';
    if (cloneTypeDisabledReason === 'duplicates') return 'cloneTypeProductsDuplicatesDesc';
    return 'cloneTypeProductsDesc';
  }, [cloneTypeDisabledReason]);

  const actionTypeLabel = useMemo(
    () => (actionType && valueLists.ACTION_TYPE?.[actionType]) ?? actionType,
    [actionType, valueLists.ACTION_TYPE],
  );

  if (!(isCrud || isClone || isCreate || isUpdateChange || changeRequestData || (!hideShowIds && !isLoading))) return null;

  return (
    <Table.ActionsBar>
      <Flex justify="space-between" gap="middle">
        <Space size="middle">
          {(isCrud || isUpdateChange || changeRequestData) && (
            <Radio.Group options={viewOptions} value={state.view} optionType="button" onChange={handleViewChange} />
          )}
          {!hideShowIds && !isLoading && (
            <Radio.Group options={showIdOptions} value={state.showIds} optionType="button" onChange={onChangeShowIds} />
          )}
          {isClone && (
            <Tooltip title={t(`pmp.products.labels.${cloneTypeTooltipKey}`)} placement="top">
              <Radio.Group options={cloneTypeOptions} value={state.cloneType} optionType="button" onChange={onChangeCloneType} />
            </Tooltip>
          )}
          {!isCrud && (
            <>
              {!!state.dataSource?.length && (
                <Text>
                  <Text strong>{t(`global.labels.totalItems`)}: </Text>
                  {state.dataSource.length}
                </Text>
              )}
              {actionTypeLabel && (
                <Text>
                  <Text strong>{t(`global.labels.type`)}: </Text>
                  {actionTypeLabel}
                </Text>
              )}
              {changeRequestData?.createdBy && (
                <Text>
                  <Text strong>{t(`global.labels.createdBy`)}: </Text>
                  {changeRequestData.createdBy}
                </Text>
              )}
            </>
          )}
        </Space>
        <Space size="middle">
          {isCreate && (
            <Button
              icon={<PlusOutlined />}
              onClick={handleAddRow}
              type="primary"
              disabled={state.isLoading || state.dataSource?.length === 10}
              loading={state.isLoading}
            >
              {t('global.buttons.addRow')}
            </Button>
          )}
        </Space>
      </Flex>
    </Table.ActionsBar>
  );
};
